import { useEffect, useState, useContext, useRef } from "react";
import { TrialDestinationNotice, TrialTestCallsLeft, TrialQuickDials, useTrialTestCalls } from '@/components/voice/TrialDialerPanel';
import { isTrialAllowedDial, isFreeTestDestination, TRIAL_OUTBOUND_MESSAGE } from '@/lib/freeTestCalls';
import { countryEntitled, decideCountryGate, pickerCountries, type CountryGate } from '@/lib/dialerCountryGate';
import { CountryGateNotice, useAccountKycVerified } from '@/components/voice/CountryGateNotice';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { MoveToMobileButton } from './MoveToMobileButton';
import { HoldForMeButton } from './HoldForMeButton';
import { usePhoneFormatter, detectCountryFromE164 } from '@/hooks/usePhoneFormatter';
import { CallQualityIndicator } from '@/components/voice/CallQualityIndicator';
import { Phone, PhoneCall, PhoneOff, Mic, MicOff, Volume2, VolumeX, Minimize2, X, Hash, Radio, Pause, Play, RotateCcw, Users, MessageSquare, BarChart3, FileText, UserPlus, Headphones, Settings, ChevronDown, ArrowRight, TrendingUp, Send, Grid3X3, Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useVoice, VoiceContext } from "@/components/voice/GlobalVoiceProvider";
import { AgentPresenceCompact } from "@/components/voice/AgentPresenceIndicator";
import { cn } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { io, Socket } from 'socket.io-client';
import { useCapabilities } from "@/hooks/useCapabilities";
import { normalizeCountryCode } from "@shared/capabilities";
import { useLocation } from "wouter";
import { writeDialerMode } from "@/lib/dialerMode";
import { gateErrorPrompt } from "@/lib/gateErrors";
import { DispositionPad } from "@/components/dialer/DispositionPad";

interface GlobalDialerProps {
  onClose?: () => void;
  initialNumber?: string;
  contactName?: string;
  className?: string;
  isModal?: boolean; // New prop to control modal behavior
  showNavigation?: boolean; // New prop to show/hide navigation controls
  startMinimized?: boolean; // Start in minimized view by default
}

export function GlobalDialer({ 
  onClose, 
  initialNumber = "", 
  contactName,
  className,
  isModal = true,
  showNavigation = true,
  startMinimized = false // Default to expanded view when opened
}: GlobalDialerProps) {
  const [selectedCallerId, setSelectedCallerId] = useState<string>("");
  const [countryCode, setCountryCode] = useState("+1");
  // The picker is keyed by ISO (+1 alone is the US, Canada and the Caribbean).
  const [pickerIso, setPickerIso] = useState("US");
  const { rawNumber: phoneNumber, formattedDisplay, handleInput, handlePaste, setRawNumber: setPhoneNumber } =
    usePhoneFormatter({ dialCode: countryCode, onDialCodeChange: setCountryCode });
  const quickDialTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isMinimized, setIsMinimized] = useState(startMinimized);
  const [showKeypad, setShowKeypad] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [isOnHold, setIsOnHold] = useState(false);
  const [showAdvancedFeatures, setShowAdvancedFeatures] = useState(false);
  const [conferenceMode, setConferenceMode] = useState(false);
  const [transferNumber, setTransferNumber] = useState("");
  const [showTransferList, setShowTransferList] = useState(false);
  const [showConferenceList, setShowConferenceList] = useState(false);
  const [whisperMessage, setWhisperMessage] = useState("");
  const [callAnalytics, setCallAnalytics] = useState(null);
  const [transcriptionEnabled, setTranscriptionEnabled] = useState(false);
  const [coachingMode, setCoachingMode] = useState(false);
  const [transferState, setTransferState] = useState<'idle' | 'consulting' | 'completing' | 'cancelling'>('idle');
  const [transferMode, setTransferMode] = useState<'cold' | 'warm'>('cold');
  const [transferTab, setTransferTab] = useState<'teammates' | 'external'>('teammates');
  const [externalTransferNumber, setExternalTransferNumber] = useState("");
  const { toast } = useToast();
  const [, navigate] = useLocation();

  /**
   * The finished call, kept so an outcome can be tagged after hang-up.
   *
   * Desktop had NO disposition surface at all -- the only one in the product was
   * the mobile sheet, and that one was posting a CallSid into a leadId slot and
   * discarding every save. PostCallSummaryDialog was the obvious host, but it
   * reads /api/voice-calls/:callSid (the Vapi `voice_calls` table) while the
   * disposition endpoint writes `calls`, so mounting the pad there would have
   * recreated the same silent no-op.
   */
  const [lastCall, setLastCall] = useState<{ number: string; callSid: string } | null>(null);

  // Check if VoiceProvider is available
  const voiceContext = useContext(VoiceContext);
  
  // Fetch capabilities for country gating
  const { capabilities, canCallCountry, balanceOK, isTrialing } = useCapabilities();
  // Trial allowlist: the AI demo line + the user's verified numbers only.
  const { data: trialTestCalls } = useTrialTestCalls(!!isTrialing);
  const kycVerified = useAccountKycVerified();

  // Fetch user call settings to get preferred default caller ID
  const { data: callSettings } = useQuery({
    queryKey: ['/api/user/call-settings'],
    enabled: !!voiceContext, // Only fetch if voice context is available
  });
  
  // Extract voice context values (with defaults if not available)
  const callState = voiceContext?.callState || { isActive: false, callSid: null };
  const makeCall = voiceContext?.makeCall || (() => {});
  const endCall = voiceContext?.endCall || (() => {});
  const isDeviceReady = voiceContext?.isDeviceReady || false;
  const connectionStatus = voiceContext?.connectionStatus || 'offline';
  const availableNumbers = voiceContext?.availableNumbers || [];
  const isMuted = voiceContext?.isMuted || false;
  const toggleMute = voiceContext?.toggleMute || (() => {});
  const hangUp = voiceContext?.hangUp || (() => {});
  const sendDigits = voiceContext?.sendDigits || (() => {});
  const callRef = voiceContext?.callRef;
  
  const callQuality = voiceContext?.callQuality || null;
  const isInCall = callState.isActive;

  /**
   * Remember a call the moment it ends so its outcome can still be tagged.
   *
   * Watches the transition rather than the status: a call that was never
   * answered keeps the BROWSER-<ts> temp SID, and no_answer / left_voicemail are
   * tagged in exactly that case. The server matches either value.
   */
  const wasInCallRef = useRef(false);
  useEffect(() => {
    if (isInCall) {
      wasInCallRef.current = true;
      setLastCall(null);
      return;
    }
    if (wasInCallRef.current) {
      wasInCallRef.current = false;
      const sid = callState.callSid;
      if (sid) setLastCall({ number: voiceContext?.callState?.number || '', callSid: sid });
    }
  }, [isInCall, callState.callSid, voiceContext?.callState?.number]);
  const currentCallSid = callState.callSid;

  const { data: recentCallLogs = [] } = useQuery<any[]>({
    queryKey: ['/api/call-logs/recent', { limit: 5 }],
    staleTime: 30000,
    enabled: !isInCall,
  });

  // Fetch organization members for transfer and conference
  const { data: orgMembers } = useQuery({
    queryKey: ['/api/organization/members'],
    enabled: !!voiceContext && isInCall, // Only fetch when voice context available and in a call
  });
  
  // Check if organization is solo (only current user, no other teammates)
  const otherTeammates = orgMembers 
    ? (orgMembers as any[]).filter((m: any) => m.userId !== voiceContext?.userId) 
    : [];
  const isSoloOrg = otherTeammates.length === 0;
  
  // If VoiceProvider is not available, don't render the dialer
  if (!voiceContext) {
    console.warn('GlobalDialer: VoiceProvider not available, skipping render');
    return null;
  }

  // Parse initialNumber to extract country code and national number
  useEffect(() => {
    const parsePhoneNumber = async () => {
      if (!initialNumber) {
        setPhoneNumber("");
        setCountryCode("+1");
        return;
      }

      try {
        const { parsePhoneNumber: parseNumber } = await import('libphonenumber-js');
        const parsed = parseNumber(initialNumber);
        
        if (parsed && parsed.isValid()) {
          const cc = `+${parsed.countryCallingCode}`;
          const nationalNum = parsed.nationalNumber.toString();
          
          setCountryCode(cc);
          setPhoneNumber(nationalNum);
        } else {
          // Fallback: try to extract country code manually
          const match = initialNumber.match(/^\+(\d{1,3})/);
          if (match) {
            setCountryCode(`+${match[1]}`);
            setPhoneNumber(initialNumber.slice(match[0].length));
          } else {
            setPhoneNumber(initialNumber);
          }
        }
      } catch (error) {
        console.error("Failed to parse phone number:", error);
        // Fallback to original behavior
        if (initialNumber.startsWith("+")) {
          const match = initialNumber.match(/^\+(\d{1,3})/);
          if (match) {
            setCountryCode(`+${match[1]}`);
            setPhoneNumber(initialNumber.slice(match[0].length));
          }
        } else {
          setPhoneNumber(initialNumber);
        }
      }
    };

    parsePhoneNumber();
  }, [initialNumber]);

  // Cleanup quick-dial timer on unmount
  useEffect(() => {
    return () => { if (quickDialTimerRef.current) clearTimeout(quickDialTimerRef.current); };
  }, []);

  // Check if recording is enabled at the phone number level when call starts
  useEffect(() => {
    const checkRecordingSettings = async () => {
      if (currentCallSid && selectedCallerId) {
        try {
          // Fetch the phone number settings to check if recording is enabled
          const phoneNumbers = await apiRequest("GET", "/api/organization/phone-numbers");
          const currentNumber = Array.isArray(phoneNumbers) ? phoneNumbers.find((n: any) => n.phoneNumber === selectedCallerId) : null;
          
          if (currentNumber?.recordingEnabled) {
            setIsRecording(true);
            // Auto-start recording if enabled at number level
            await apiRequest("POST", `/api/calls/${currentCallSid}/recording/start`, {
              recordingChannels: 'dual',
              trim: 'trim-silence',
              playBeep: false
            });
          } else {
            setIsRecording(false);
          }
        } catch (error) {
          console.error("Failed to check recording settings:", error);
        }
      } else if (!isInCall) {
        // Reset recording state when call ends
        setIsRecording(false);
      }
    };

    checkRecordingSettings();
  }, [currentCallSid, isInCall, selectedCallerId]);

  // Advanced Call Management Functions
  const toggleRecording = async () => {
    if (!currentCallSid) return;
    
    try {
      if (isRecording) {
        await apiRequest("POST", `/api/calls/${currentCallSid}/recording/stop`);
        setIsRecording(false);
        toast({ title: "Recording stopped", description: "Call recording has been stopped." });
      } else {
        await apiRequest("POST", `/api/calls/${currentCallSid}/recording/start`, {
          recordingChannels: 'dual',
          trim: 'trim-silence',
          playBeep: false
        });
        setIsRecording(true);
        toast({ title: "Recording started", description: "Call recording has been started." });
      }
    } catch (error) {
      console.error("Failed to toggle recording:", error);
      const gate = gateErrorPrompt(error);
      toast({ title: gate ? gate.title : "Recording error", description: gate ? gate.message : "Failed to toggle recording.", variant: "destructive" });
    }
  };

  const holdCall = async () => {
    if (!currentCallSid) {
      console.error("❌ [FRONTEND-HOLD] No active call SID for hold operation");
      return;
    }
    
    try {
      console.log(`🔄 [FRONTEND-HOLD] ============ INITIATING HOLD ============`);
      console.log(`🔄 [FRONTEND-HOLD] Call SID: ${currentCallSid}`);
      console.log(`🔄 [FRONTEND-HOLD] Call State:`, callState);
      console.log(`🔄 [FRONTEND-HOLD] Is In Call: ${isInCall}`);
      console.log(`🔄 [FRONTEND-HOLD] Current Status: ${callState.status}`);
      console.log(`🔄 [FRONTEND-HOLD] Timestamp: ${new Date().toISOString()}`);
      
      // Use server-side hold for proper hold music and experience
      console.log(`📡 [FRONTEND-HOLD] Sending POST request to /api/calls/${currentCallSid}/hold...`);
      const response: any = await apiRequest("POST", `/api/calls/${currentCallSid}/hold`);
      console.log(`✅ [FRONTEND-HOLD] Server response received:`, response);
      
      setIsOnHold(true);
      console.log(`✅ [FRONTEND-HOLD] UI state updated: isOnHold = true`);
      console.log(`✅ [FRONTEND-HOLD] ============ HOLD COMPLETE ============`);
      
      toast({ 
        title: "Call on hold", 
        description: "You'll hear hold music while waiting." 
      });
    } catch (error) {
      console.error("❌ [FRONTEND-HOLD] ============ HOLD FAILED ============");
      console.error("❌ [FRONTEND-HOLD] Error:", error);
      console.error("❌ [FRONTEND-HOLD] Error message:", (error as Error)?.message);
      console.error("❌ [FRONTEND-HOLD] Error stack:", (error as Error)?.stack);
      toast({ title: "Hold error", description: "Failed to place call on hold.", variant: "destructive" });
    }
  };

  const resumeCall = async () => {
    if (!currentCallSid) {
      console.error("❌ [FRONTEND-RESUME] No active call SID for resume operation");
      return;
    }
    
    try {
      console.log(`🔄 [FRONTEND-RESUME] ============ INITIATING RESUME ============`);
      console.log(`🔄 [FRONTEND-RESUME] Call SID: ${currentCallSid}`);
      console.log(`🔄 [FRONTEND-RESUME] Call State:`, callState);
      console.log(`🔄 [FRONTEND-RESUME] Is In Call: ${isInCall}`);
      console.log(`🔄 [FRONTEND-RESUME] Current Status: ${callState.status}`);
      console.log(`🔄 [FRONTEND-RESUME] Is On Hold: ${isOnHold}`);
      console.log(`🔄 [FRONTEND-RESUME] Timestamp: ${new Date().toISOString()}`);
      
      // Use server-side resume for proper call management
      console.log(`📡 [FRONTEND-RESUME] Sending POST request to /api/calls/${currentCallSid}/resume...`);
      const response: any = await apiRequest("POST", `/api/calls/${currentCallSid}/resume`);
      console.log(`✅ [FRONTEND-RESUME] Server response received:`, response);
      
      setIsOnHold(false);
      console.log(`✅ [FRONTEND-RESUME] UI state updated: isOnHold = false`);
      
      // Log connection state after resume
      if (callRef?.current) {
        console.log(`📞 [FRONTEND-RESUME] Twilio connection status:`, callRef.current.status());
        console.log(`📞 [FRONTEND-RESUME] Twilio connection parameters:`, callRef.current.parameters);
      } else {
        console.warn(`⚠️ [FRONTEND-RESUME] No Twilio connection reference available`);
      }
      
      console.log(`✅ [FRONTEND-RESUME] ============ RESUME COMPLETE ============`);
      
      toast({ 
        title: "Call resumed", 
        description: "Your call is now active." 
      });
    } catch (error) {
      console.error("❌ [FRONTEND-RESUME] ============ RESUME FAILED ============");
      console.error("❌ [FRONTEND-RESUME] Error:", error);
      console.error("❌ [FRONTEND-RESUME] Error message:", (error as Error)?.message);
      console.error("❌ [FRONTEND-RESUME] Error stack:", (error as Error)?.stack);
      toast({ title: "Resume error", description: "Failed to resume call.", variant: "destructive" });
    }
  };

  const transferCall = async (type: 'cold' | 'warm' = 'cold', target?: string | { userId: string; name: string }) => {
    if (!currentCallSid) {
      toast({ title: "Transfer error", description: "No active call.", variant: "destructive" });
      return;
    }
    
    // Determine transfer target
    let transferTo: string;
    let targetName: string = "target";
    
    if (typeof target === 'object' && target?.userId) {
      // Browser-to-browser transfer using client identifier
      transferTo = `client:${target.userId}`;
      targetName = target.name;
    } else if (typeof target === 'string' && target.trim()) {
      // External number transfer
      transferTo = target.trim();
      targetName = transferTo;
    } else if (externalTransferNumber.trim()) {
      // Use external number input
      transferTo = externalTransferNumber.trim();
      targetName = transferTo;
    } else {
      toast({ 
        title: "Transfer error", 
        description: "Please select a teammate or enter a phone number.", 
        variant: "destructive" 
      });
      return;
    }
    
    try {
      setTransferMode(type);
      
      if (type === 'cold') {
        // Cold transfer - direct transfer without consultation
        await apiRequest("POST", `/api/voice/transfer/cold`, {
          callSid: currentCallSid,
          transferTo: transferTo,
          fromNumber: selectedCallerId
        });
        
        setExternalTransferNumber("");
        setShowTransferList(false);
        toast({ 
          title: "Transfer initiated", 
          description: `Transferring to ${targetName}. You will be disconnected when they answer.` 
        });
      } else {
        // Warm transfer - initiate consultation
        await apiRequest("POST", `/api/voice/transfer/warm/initiate`, {
          callSid: currentCallSid,
          transferTo: transferTo,
          fromNumber: selectedCallerId
        });
        
        setTransferState('consulting');
        setShowTransferList(false);
        toast({ 
          title: "Warm transfer initiated", 
          description: `Consulting with ${targetName}. Complete or cancel when ready.` 
        });
      }
    } catch (error) {
      console.error("Failed to transfer call:", error);
      toast({ 
        title: "Transfer error", 
        description: "Failed to transfer call. Please try again.", 
        variant: "destructive" 
      });
      setTransferState('idle');
    }
  };

  const completeWarmTransfer = async () => {
    if (!currentCallSid) return;
    
    try {
      setTransferState('completing');
      await apiRequest("POST", `/api/voice/transfer/warm/complete`, {
        callSid: currentCallSid
      });
      
      setTransferState('idle');
      setTransferNumber("");
      toast({ 
        title: "Transfer completed", 
        description: "Call has been transferred successfully." 
      });
    } catch (error) {
      console.error("Failed to complete transfer:", error);
      toast({ title: "Transfer error", description: "Failed to complete transfer.", variant: "destructive" });
      setTransferState('consulting');
    }
  };

  const cancelWarmTransfer = async () => {
    if (!currentCallSid) return;
    
    try {
      setTransferState('cancelling');
      await apiRequest("POST", `/api/voice/transfer/warm/cancel`, {
        callSid: currentCallSid
      });
      
      setTransferState('idle');
      setTransferNumber("");
      toast({ 
        title: "Transfer cancelled", 
        description: "Transfer cancelled. You are back on the call with the original caller." 
      });
    } catch (error) {
      console.error("Failed to cancel transfer:", error);
      toast({ title: "Transfer error", description: "Failed to cancel transfer.", variant: "destructive" });
      setTransferState('consulting');
    }
  };

  const escalateCall = async () => {
    if (!currentCallSid || !transferNumber.trim()) {
      toast({ title: "Escalation error", description: "Please enter a supervisor number.", variant: "destructive" });
      return;
    }
    
    try {
      await apiRequest("POST", `/api/calls/${currentCallSid}/escalate`, {
        supervisorNumber: transferNumber,
        escalationType: 'supervisor',
        urgent: false
      });
      
      setTransferNumber("");
      toast({ title: "Call escalated", description: "Call has been escalated to supervisor." });
    } catch (error) {
      console.error("Failed to escalate call:", error);
      toast({ title: "Escalation error", description: "Failed to escalate call.", variant: "destructive" });
    }
  };

  // Toggle Functions for Main Call Interface
  const toggleHold = async () => {
    if (isOnHold) {
      await resumeCall();
    } else {
      await holdCall();
    }
  };

  const createConference = async () => {
    if (!currentCallSid) return;
    
    try {
      const conferenceName = `conf-${Date.now()}`;
      await apiRequest("POST", "/api/calls/conference/create", {
        conferenceName,
        moderator: true,
        muted: false,
        record: isRecording,
        maxParticipants: 10
      });
      
      await apiRequest("POST", `/api/calls/${currentCallSid}/conference/join`, {
        conferenceName,
        muted: false,
        moderator: true
      });
      
      setConferenceMode(true);
      toast({ title: "Conference created", description: "Conference call has been created." });
    } catch (error) {
      console.error("Failed to create conference:", error);
      toast({ title: "Conference error", description: "Failed to create conference.", variant: "destructive" });
    }
  };

  const addToConference = async (target?: string | { userId: string; name: string }) => {
    if (!currentCallSid) {
      toast({ title: "Conference error", description: "No active call.", variant: "destructive" });
      return;
    }
    
    // Determine participant target
    let participantTarget: string;
    let targetName: string = "participant";
    
    if (typeof target === 'object' && target?.userId) {
      // Browser-to-browser using client identifier
      participantTarget = `client:${target.userId}`;
      targetName = target.name;
    } else if (typeof target === 'string' && target.trim()) {
      // External number
      participantTarget = target.trim();
      targetName = participantTarget;
    } else {
      toast({ 
        title: "Conference error", 
        description: "Please select a teammate.", 
        variant: "destructive" 
      });
      return;
    }
    
    try {
      await apiRequest("POST", `/api/calls/${currentCallSid}/conference/add-participant`, {
        participantNumber: participantTarget,
        muted: false
      });
      
      setShowConferenceList(false);
      toast({ 
        title: "Adding to conference", 
        description: `Dialing ${targetName}...` 
      });
    } catch (error) {
      console.error("Failed to add to conference:", error);
      toast({ 
        title: "Conference error", 
        description: "Failed to add participant.", 
        variant: "destructive" 
      });
    }
  };

  const sendWhisper = async () => {
    if (!currentCallSid || !whisperMessage) return;
    
    try {
      await apiRequest("POST", `/api/calls/${currentCallSid}/whisper`, {
        message: whisperMessage,
        voice: 'alice'
      });
      toast({ title: "Whisper sent", description: "Whisper message has been sent." });
      setWhisperMessage("");
    } catch (error) {
      console.error("Failed to send whisper:", error);
      toast({ title: "Whisper error", description: "Failed to send whisper.", variant: "destructive" });
    }
  };

  const enableTranscription = async () => {
    if (!currentCallSid) return;
    
    try {
      await apiRequest("POST", `/api/calls/${currentCallSid}/transcription/enable`, {
        language: 'en-US',
        speechModel: 'phone_call',
        enhanced: true
      });
      setTranscriptionEnabled(true);
      toast({ title: "Transcription enabled", description: "Call transcription has been enabled." });
    } catch (error) {
      console.error("Failed to enable transcription:", error);
      const gate = gateErrorPrompt(error);
      toast({ title: gate ? gate.title : "Transcription error", description: gate ? gate.message : "Failed to enable transcription.", variant: "destructive" });
    }
  };

  const getCallAnalytics = async () => {
    if (!currentCallSid) return;
    
    try {
      const analytics = await apiRequest("GET", `/api/calls/${currentCallSid}/analytics`);
      setCallAnalytics(analytics);
      toast({ title: "Analytics loaded", description: "Call analytics have been loaded." });
    } catch (error) {
      console.error("Failed to get analytics:", error);
      toast({ title: "Analytics error", description: "Failed to load analytics.", variant: "destructive" });
    }
  };

  const startCoaching = async () => {
    if (!currentCallSid) return;
    
    try {
      // This would typically use a coach's phone number
      const coachingNumber = "+1234567890"; // Replace with actual coach number
      await apiRequest("POST", `/api/calls/${currentCallSid}/coaching/start`, {
        coachingSid: coachingNumber
      });
      setCoachingMode(true);
      toast({ title: "Coaching started", description: "Call coaching has been started." });
    } catch (error) {
      console.error("Failed to start coaching:", error);
      toast({ title: "Coaching error", description: "Failed to start coaching.", variant: "destructive" });
    }
  };

  useEffect(() => {
    if (availableNumbers.length > 0 && !selectedCallerId) {
      // Create combined list of available caller IDs (Twilio numbers + verified numbers)
      const verifiedNumbers = (callSettings as any)?.verifiedNumbers?.map((n: any) => n.number) || [];
      const allAvailableCallerIds = [...availableNumbers, ...verifiedNumbers];
      
      // Use user's preferred default caller ID if available, otherwise use first available number
      const defaultCallerId = (callSettings as any)?.defaultCallerId || availableNumbers[0];
      
      // Check if the default caller ID is available in our combined list
      const callerIdToUse = allAvailableCallerIds.includes(defaultCallerId) 
        ? defaultCallerId 
        : availableNumbers[0];
        
      setSelectedCallerId(callerIdToUse);
      console.log(`📞 [CALLER ID] Set default caller ID: ${callerIdToUse} (preferred: ${(callSettings as any)?.defaultCallerId}, available: ${allAvailableCallerIds.join(', ')})`);
    }
  }, [availableNumbers, selectedCallerId, callSettings]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isInCall) {
      interval = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      setCallDuration(0);
      // Reset transfer state when call ends
      setTransferState('idle');
    }
    return () => clearInterval(interval);
  }, [isInCall]);

  // Auto-select external tab if solo organization
  useEffect(() => {
    if (isSoloOrg && showTransferList && transferTab === 'teammates') {
      setTransferTab('external');
    }
  }, [isSoloOrg, showTransferList, transferTab]);

  // Socket.IO listener for transfer failures
  useEffect(() => {
    if (!voiceContext?.userId) return;
    
    const socket: Socket = io('', {
      auth: { userId: voiceContext.userId },
      transports: ['websocket', 'polling']
    });
    
    socket.on('connect', () => {
      console.log('📡 [SOCKET] Connected for transfer events');
      const token = localStorage.getItem('token');
      if (token) {
        socket.emit('auth', { token });
      }
    });
    
    socket.on('transfer_failed', (data: { callSid: string; reason: string }) => {
      console.log('❌ [SOCKET] Transfer failed event received:', data);
      
      // Only handle if this is the current call
      if (data.callSid === currentCallSid) {
        toast({
          title: "Transfer failed",
          description: `Transfer failed: ${data.reason}. You are back on the call.`,
          variant: "destructive"
        });
        
        // Reset transfer state to allow retry
        setTransferState('idle');
        setTransferMode('cold');
        setExternalTransferNumber('');
        setShowTransferList(false);
      }
    });
    
    return () => {
      socket.off('transfer_failed');
      socket.disconnect();
    };
  }, [voiceContext?.userId, currentCallSid, toast]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleNumberInput = (digit: string) => {
    if (isInCall) {
      // Send DTMF tone during active call
      sendDigits(digit);
      console.log('📞 [GLOBAL-DIALER] Sent DTMF digit:', digit);
    } else {
      // Append digit — setPhoneNumber (setRawNumber) expects a string, not a function updater
      setPhoneNumber(phoneNumber + digit);
    }
  };

  const handleCall = async () => {
    if (!phoneNumber.trim()) return;
    
    // Check balance status
    if (!balanceOK) {
      toast({
        title: "Payment Required",
        description: "Your account balance is insufficient or suspended. Please update your payment method.",
        variant: "destructive",
      });
      return;
    }
    
    // Check if phone numbers are available
    if (!availableNumbers || availableNumbers.length === 0) {
      console.error('No phone numbers available for calling');
      return;
    }
    
    let fullNumber = phoneNumber.trim();
    
    // Add country code if not already present
    if (!fullNumber.startsWith('+')) {
      fullNumber = `${countryCode === '+1CA' ? '+1' : countryCode}${fullNumber}`;
    }

    // Country gate (lib/dialerCountryGate): free test destination → KYC →
    // trial. Advisory; the server re-checks every call.
    const gate = countryGateFor(fullNumber);
    if (gate && gate.kind !== 'allowed') {
      if (gate.kind === 'trial' && gate.domestic) {
        toast({ title: 'Trial calling', description: TRIAL_OUTBOUND_MESSAGE, variant: 'destructive' });
      }
      return; // the inline CountryGateNotice / TrialDestinationNotice explains it
    }
    // Unparseable number on a trial: the #1537 allowlist still applies.
    if (!gate && isTrialing && trialTestCalls && !isTrialAllowedDial(fullNumber, trialTestCalls)) {
      toast({ title: 'Trial calling', description: TRIAL_OUTBOUND_MESSAGE, variant: 'destructive' });
      return;
    }

    try {
      await makeCall(fullNumber, { fromNumber: selectedCallerId });
    } catch (error) {
      console.error('Call failed:', error);
    }
  };

  const handleEndCall = async () => {
    if (hangUp) {
      await hangUp();
    } else {
      await endCall();
    }
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const toggleDialpad = () => {
    setShowKeypad(!showKeypad);
  };



  const clearNumber = () => {
    setPhoneNumber("");
  };

  const backspace = () => {
    setPhoneNumber(phoneNumber.slice(0, -1));
  };

  const keypadButtons = [
    { label: "1", value: "1" },
    { label: "2", value: "2", sub: "ABC" },
    { label: "3", value: "3", sub: "DEF" },
    { label: "4", value: "4", sub: "GHI" },
    { label: "5", value: "5", sub: "JKL" },
    { label: "6", value: "6", sub: "MNO" },
    { label: "7", value: "7", sub: "PQRS" },
    { label: "8", value: "8", sub: "TUV" },
    { label: "9", value: "9", sub: "WXYZ" },
    { label: "*", value: "*" },
    { label: "0", value: "0", sub: "+" },
    { label: "#", value: "#" },
  ];

  // Every country, the ones this account may call listed first.
  const countryCodes = pickerCountries(capabilities);
  const dialOf = (code: string) => (code === '+1CA' ? '+1' : code);
  const selectedIso = countryCodes.some((c) => c.iso === pickerIso && c.dialCode === dialOf(countryCode))
    ? pickerIso
    : (countryCode === '+1CA' ? 'CA' : countryCodes.find((c) => c.dialCode === dialOf(countryCode) && (c.iso === 'US' || c.dialCode !== '+1'))?.iso || 'US');

  /** The gate for a full number, or null when it can't be parsed (the server decides). */
  function countryGateFor(fullNumber: string): CountryGate | null {
    const parsed = parsePhoneNumberFromString(fullNumber);
    const iso = parsed?.country;
    if (!iso || !capabilities) return null;
    const country = countryCodes.find((c) => c.iso === iso);
    return decideCountryGate({
      iso,
      countryName: country?.name,
      isTestDestination: isTrialAllowedDial(fullNumber, trialTestCalls) || isFreeTestDestination(fullNumber, trialTestCalls),
      entitled: countryEntitled(capabilities, iso),
      trialing: !!isTrialing,
      kycVerified,
    });
  }

  const getDisplayNumber = () => {
    if (!phoneNumber) return "";
    
    // If it's a full international number, display as is
    if (phoneNumber.startsWith('+')) {
      return phoneNumber;
    }
    
    // Otherwise, format with country code
    return `${countryCode}${phoneNumber}`;
  };

  const displayNumber = getDisplayNumber();
  // What the dialer says right now: the typed number's country once it parses,
  // else the picked country (so a locked pick explains itself before typing).
  const liveNumber = phoneNumber.startsWith('+') ? phoneNumber : `${dialOf(countryCode)}${phoneNumber}`;
  const liveCountryGate: CountryGate | null = !capabilities ? null : phoneNumber.replace(/[^0-9]/g, '').length >= 7
    ? countryGateFor(liveNumber)
    : decideCountryGate({
      iso: selectedIso,
      countryName: countryCodes.find((c) => c.iso === selectedIso)?.name,
      isTestDestination: false,
      entitled: countryEntitled(capabilities, selectedIso),
      trialing: !!isTrialing,
      kycVerified,
    });
  const displayName = contactName || callState.contact?.name || "";

  if (isMinimized && isModal) {
    // Compact minimal view during calls
    if (isInCall) {
      return (
        <div className="fixed top-16 right-4 z-50 pointer-events-auto">
          <div className="w-80 bg-background border border-border text-text-layer-primary shadow-2xl rounded-2xl overflow-hidden">
            {/* Header - minimal with just status and timer */}
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2">
                <div className={cn("w-2 h-2 rounded-full", isDeviceReady ? "bg-green-500" : "bg-red-500")}></div>
                <span className="text-green-400 font-medium">{formatDuration(callDuration)}</span>
                {isMuted && <MicOff className="w-4 h-4 text-red-400" />}
                {isOnHold && <Pause className="w-4 h-4 text-amber-400" />}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowConferenceList(!showConferenceList)}
                  className="text-text-layer-muted hover:text-text-layer-primary p-1 h-auto"
                  title="Add participant"
                >
                  <UserPlus className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleMinimize}
                  className="text-text-layer-muted hover:text-text-layer-primary p-1 h-auto"
                  title="Expand"
                >
                  <Maximize2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Connected caller */}
            <div className="px-4 pb-3">
              <div className="flex items-center justify-between bg-surface-elevated/50 rounded-lg px-3 py-2">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-text-layer-primary text-xs">
                    <Phone className="w-4 h-4" />
                  </div>
                  <span className="font-medium">{contactName || displayNumber || "Caller"}</span>
                </div>
              </div>
            </div>

            {/* Bottom toolbar */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-surface-elevated/30">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleMute}
                className={cn(
                  "rounded-lg p-2 h-auto",
                  isMuted ? "bg-green-600 text-text-layer-primary" : "bg-surface-elevated text-text-layer-secondary hover:bg-surface-hover"
                )}
                title={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={toggleHold}
                className={cn(
                  "rounded-lg p-2 h-auto",
                  isOnHold ? "bg-amber-600 text-text-layer-primary" : "bg-surface-elevated text-text-layer-secondary hover:bg-surface-hover"
                )}
                title={isOnHold ? "Resume" : "Hold"}
              >
                {isOnHold ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowTransferList(!showTransferList)}
                className="bg-surface-elevated text-text-layer-secondary hover:bg-surface-hover rounded-lg p-2 h-auto"
                title="Transfer"
              >
                <ArrowRight className="w-5 h-5" />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={toggleMinimize}
                className="bg-surface-elevated text-text-layer-secondary hover:bg-surface-hover rounded-lg p-2 h-auto"
                title="Show keypad"
              >
                <Grid3X3 className="w-5 h-5" />
              </Button>

              <Button
                variant="destructive"
                size="sm"
                onClick={handleEndCall}
                className="bg-red-600 hover:bg-red-700 rounded-lg p-2 h-auto"
                title="End call"
              >
                <PhoneOff className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // Minimized bubble when not in call
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <div className="w-16 h-16 bg-background border border-border text-text-layer-primary shadow-2xl rounded-full flex items-center justify-center cursor-pointer hover:bg-surface-elevated transition-colors"
             onClick={toggleMinimize}>
          <Phone className="w-6 h-6" />
        </div>
      </div>
    );
  }

  const dialerContent = (
    <div className={cn(
      "bg-background border border-border text-text-layer-primary shadow-2xl overflow-hidden",
      isModal ? "w-80 rounded-2xl" : "w-full h-full rounded-none",
      className
    )}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-border">
        <div className="flex items-center gap-2">
          <div className={cn("w-2 h-2 rounded-full", 
            isDeviceReady ? "bg-green-500" : "bg-red-500"
          )}></div>
          {isInCall && (
            <span className="text-sm text-green-400 font-medium">
              {formatDuration(callDuration)}
            </span>
          )}
          {/* Agent Presence Status */}
          <AgentPresenceCompact />

          {/* Simple | Power. Hidden mid-call: switching surfaces while a leg is
              up would leave the rep hunting for the hang-up button. */}
          {!isInCall && (
            <div className="flex items-center rounded-full bg-surface-elevated p-0.5" role="group" aria-label="Dialer mode">
              <button
                type="button"
                onClick={() => writeDialerMode('simple')}
                aria-pressed={true}
                className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-primary text-primary-foreground"
                data-testid="btn-dialer-mode-simple"
              >
                Simple
              </button>
              <button
                type="button"
                onClick={() => {
                  writeDialerMode('power');
                  onClose?.();
                  navigate('/dialer');
                }}
                className="px-2 py-0.5 text-[11px] font-medium rounded-full text-text-layer-muted hover:text-text-layer-primary transition-colors"
                title="Work a contact list end to end"
                data-testid="btn-dialer-mode-power"
              >
                Power
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          {showNavigation && (
            <>
              {/* Recording Toggle */}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={toggleRecording}
                className={cn(
                  "hover:bg-surface-elevated p-1 h-auto",
                  isRecording ? "text-red-400" : "text-text-layer-muted"
                )}
                title={isRecording ? "Stop Recording" : "Start Recording"}
              >
                <Radio className="w-4 h-4" />
              </Button>
            </>
          )}
          
          {/* Minimize - always visible in modal mode */}
          {isModal && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={toggleMinimize}
              className="text-text-layer-muted hover:bg-surface-elevated hover:text-text-layer-primary p-1.5 h-auto"
              title="Minimize to floating button"
              data-testid="btn-minimize-dialer"
            >
              <Minimize2 className="w-4 h-4" />
            </Button>
          )}
          
          {/* Close */}
          {onClose && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClose} 
              className="text-text-layer-primary hover:bg-surface-elevated p-1.5 h-auto"
              title="Close Dialer"
              data-testid="btn-close-dialer"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 space-y-4">
        {/* Outcome of the call that just ended. Desktop had no way to tag one. */}
        {!isInCall && lastCall && (
          <div className="rounded-xl border border-border p-3 space-y-2" data-testid="post-call-disposition">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wider text-text-layer-muted">
                  How did it go?
                </div>
                <div className="text-sm truncate">{lastCall.number}</div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLastCall(null)}
                className="text-text-layer-muted hover:bg-surface-elevated p-1 h-auto"
                title="Skip tagging this call"
                data-testid="btn-dismiss-disposition"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
            <DispositionPad
              callSid={lastCall.callSid}
              onSaved={(value) => {
                setLastCall(null);
                toast({ title: 'Outcome saved', description: value.replace(/_/g, ' ') });
              }}
              onError={(message) =>
                toast({ title: 'Outcome not saved', description: message, variant: 'destructive' })
              }
            />
          </div>
        )}

        {/* Contact Info */}
        <div className="text-center">
          <div className="text-lg font-semibold mb-1">
            {displayName}
          </div>
          <div className="text-text-layer-muted text-sm">
            {displayNumber || "Enter a phone number"}
          </div>
        </div>

        {/* Call Status */}
        {isInCall && (
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-green-400 text-sm">
                {callState.status === 'ringing' ? 'Ringing...' :
                 callState.status === 'connected' ? '● Connected' : 'Calling...'}
              </span>
              {callState.status === 'connected' && <CallQualityIndicator quality={callQuality} />}
            </div>
            {callState.status === 'connected' && (
              <MoveToMobileButton callSid={currentCallSid || undefined} callQuality={callQuality} className="mb-2" />
            )}
            {callState.status === 'connected' && (callState as any).direction === 'outbound' && currentCallSid && !String(currentCallSid).startsWith('temp') && (
              <HoldForMeButton callSid={currentCallSid} className="mb-2" />
            )}
            <div className="text-lg font-mono">
              {formatDuration(callDuration)}
            </div>
            {/* Call Status Indicators */}
            <div className="flex justify-center gap-2 mt-2">
              {isRecording && (
                <span className="text-xs bg-red-500 text-text-layer-primary px-2 py-1 rounded">
                  Recording
                </span>
              )}
              {isOnHold && (
                <span className="text-xs bg-yellow-500 text-text-layer-primary px-2 py-1 rounded">
                  On Hold
                </span>
              )}
              {transferState === 'consulting' && (
                <span className="text-xs bg-blue-500 text-text-layer-primary px-2 py-1 rounded">
                  Consulting
                </span>
              )}
              {conferenceMode && (
                <span className="text-xs bg-blue-500 text-text-layer-primary px-2 py-1 rounded">
                  Conference
                </span>
              )}
              {transcriptionEnabled && (
                <span className="text-xs bg-primary text-text-layer-primary px-2 py-1 rounded">
                  Transcription
                </span>
              )}
              {coachingMode && (
                <span className="text-xs bg-green-500 text-text-layer-primary px-2 py-1 rounded">
                  Coaching
                </span>
              )}
            </div>
          </div>
        )}

        {/* Warm Transfer Consultation Controls */}
        {isInCall && transferState === 'consulting' && (
          <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <RotateCcw className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-medium text-blue-200">Warm Transfer in Progress</span>
            </div>
            <p className="text-xs text-blue-300 mb-4">
              You're consulting with the transfer target. The caller is on hold. Complete the transfer or cancel to resume with the original caller.
            </p>
            <div className="flex gap-2">
              <Button
                variant="default"
                size="sm"
                onClick={completeWarmTransfer}
                disabled={transferState !== 'consulting'}
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="button-complete-warm-transfer"
              >
                <Send className="w-3.5 h-3.5 mr-1.5" />
                Complete Transfer
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={cancelWarmTransfer}
                disabled={transferState !== 'consulting'}
                className="flex-1 border-border hover:bg-surface-elevated"
                data-testid="button-cancel-warm-transfer"
              >
                <X className="w-3.5 h-3.5 mr-1.5" />
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Modern Call Control Panel */}
        {isInCall && (
          <div className="bg-background/50 rounded-lg border border-border/50 backdrop-blur-sm">
            {/* Quick Actions Row */}
            <div className="flex items-center justify-between p-3 border-b border-border/30">
              <div className="flex items-center gap-2">
                {/* Recording Toggle */}
                <Button
                  variant={isRecording ? "destructive" : "ghost"}
                  size="sm"
                  onClick={toggleRecording}
                  className={`h-8 px-3 text-xs transition-all ${
                    isRecording 
                      ? 'bg-red-600 hover:bg-red-700 text-text-layer-primary shadow-red-500/25 shadow-lg' 
                      : 'hover:bg-surface-elevated text-text-layer-secondary hover:text-text-layer-primary'
                  }`}
                >
                  <Radio className="w-3.5 h-3.5 mr-1.5" />
                  {isRecording ? 'Recording' : 'Record'}
                </Button>

                {/* Hold/Resume Toggle */}
                <Button
                  variant={isOnHold ? "secondary" : "ghost"}
                  size="sm"
                  onClick={isOnHold ? resumeCall : holdCall}
                  disabled={transferState === 'consulting'}
                  className={`h-8 px-3 text-xs transition-all ${
                    isOnHold 
                      ? 'bg-yellow-600 hover:bg-yellow-700 text-text-layer-primary shadow-yellow-500/25 shadow-lg' 
                      : 'hover:bg-surface-elevated text-text-layer-secondary hover:text-text-layer-primary'
                  }`}
                  data-testid="button-toggle-hold"
                >
                  {isOnHold ? <Play className="w-3.5 h-3.5 mr-1.5" /> : <Pause className="w-3.5 h-3.5 mr-1.5" />}
                  {isOnHold ? 'Resume' : 'Hold'}
                </Button>
              </div>

              {/* Advanced Features Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAdvancedFeatures(!showAdvancedFeatures)}
                className="h-8 px-3 text-xs hover:bg-surface-elevated text-text-layer-muted hover:text-text-layer-primary transition-all"
              >
                <Settings className="w-3.5 h-3.5 mr-1.5" />
                More
                <ChevronDown className={`w-3 h-3 ml-1.5 transition-transform ${showAdvancedFeatures ? 'rotate-180' : ''}`} />
              </Button>
            </div>

            {/* Advanced Features Panel */}
            {showAdvancedFeatures && (
              <div className="p-4 space-y-4">
                {/* Transfer Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium text-text-layer-primary">
                      <RotateCcw className="w-4 h-4 text-blue-400" />
                      Transfer Call
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowTransferList(!showTransferList)}
                      className="h-7 px-2 text-xs hover:bg-surface-elevated"
                    >
                      <ChevronDown className={`w-3 h-3 transition-transform ${showTransferList ? 'rotate-180' : ''}`} />
                    </Button>
                  </div>
                  
                  {showTransferList && (
                    <div className="space-y-3">
                      {/* Transfer Tabs - Hide teammates tab if solo org */}
                      {!isSoloOrg && (
                        <div className="flex gap-1 bg-surface-elevated rounded p-1">
                          <button
                            onClick={() => setTransferTab('teammates')}
                            className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
                              transferTab === 'teammates' 
                                ? 'bg-blue-600 text-text-layer-primary' 
                                : 'text-text-layer-muted hover:text-text-layer-primary'
                            }`}
                          >
                            Teammates
                          </button>
                          <button
                            onClick={() => setTransferTab('external')}
                            className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
                              transferTab === 'external' 
                                ? 'bg-blue-600 text-text-layer-primary' 
                                : 'text-text-layer-muted hover:text-text-layer-primary'
                            }`}
                          >
                            External
                          </button>
                        </div>
                      )}

                      {/* Teammates Tab */}
                      {transferTab === 'teammates' && orgMembers && !isSoloOrg && (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {otherTeammates.map((member: any) => {
                            const isAvailable = member.user?.presence?.status === 'available';
                            const isBusy = member.user?.presence?.status === 'busy';
                            const isOffline = !member.user?.presence?.status || member.user?.presence?.status === 'offline';
                            const canTransfer = isAvailable || isBusy; // Can transfer to available or busy teammates
                            
                            return (
                              <div key={member.id} className="flex gap-2 items-center">
                                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                  isAvailable ? 'bg-green-500' : 
                                  isBusy ? 'bg-yellow-500' : 
                                  'bg-gray-500'
                                }`} />
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => transferCall('cold', { 
                                    userId: member.userId, 
                                    name: member.user?.fullName || member.user?.email || 'teammate' 
                                  })}
                                  disabled={!canTransfer}
                                  className="flex-1 justify-start h-9 text-xs border-border hover:bg-blue-600 hover:border-blue-500 hover:text-text-layer-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                  data-testid={`button-transfer-cold-${member.id}`}
                                  title={!canTransfer ? 'Teammate is offline' : ''}
                                >
                                  <ArrowRight className="w-3.5 h-3.5 mr-1.5" />
                                  <span className="truncate">{member.user?.fullName || member.user?.email}</span>
                                  <span className="ml-auto text-xs text-text-layer-muted">Cold</span>
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => transferCall('warm', { 
                                    userId: member.userId, 
                                    name: member.user?.fullName || member.user?.email || 'teammate' 
                                  })}
                                  disabled={!canTransfer}
                                  className="h-9 px-3 text-xs border-border hover:bg-green-600 hover:border-green-500 hover:text-text-layer-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                  data-testid={`button-transfer-warm-${member.id}`}
                                  title={!canTransfer ? 'Teammate is offline' : ''}
                                >
                                  <Users className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            );
                          })}
                          {(!orgMembers || (orgMembers as any[]).filter((m: any) => m.userId !== voiceContext?.userId).length === 0) && (
                            <p className="text-xs text-text-layer-muted text-center py-2">No other team members available</p>
                          )}
                        </div>
                      )}

                      {/* External Tab - Always show for external transfers */}
                      {(transferTab === 'external' || isSoloOrg) && (
                        <div className="space-y-2">
                          <Input
                            value={externalTransferNumber}
                            onChange={(e) => setExternalTransferNumber(e.target.value)}
                            placeholder="+1234567890"
                            className="bg-surface-elevated border-border text-text-layer-primary text-sm"
                          />
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => transferCall('cold')}
                              disabled={!externalTransferNumber.trim()}
                              className="flex-1 h-9 text-xs border-border hover:bg-blue-600 hover:border-blue-500 hover:text-text-layer-primary"
                            >
                              <ArrowRight className="w-3.5 h-3.5 mr-1.5" />
                              Cold Transfer
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => transferCall('warm')}
                              disabled={!externalTransferNumber.trim()}
                              className="flex-1 h-9 text-xs border-border hover:bg-green-600 hover:border-green-500 hover:text-text-layer-primary"
                            >
                              <Users className="w-3.5 h-3.5 mr-1.5" />
                              Warm Transfer
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Conference Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium text-text-layer-primary">
                      <Users className="w-4 h-4 text-primary" />
                      Add to Conference
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowConferenceList(!showConferenceList)}
                      className="h-7 px-2 text-xs hover:bg-surface-elevated"
                    >
                      <ChevronDown className={`w-3 h-3 transition-transform ${showConferenceList ? 'rotate-180' : ''}`} />
                    </Button>
                  </div>
                  
                  {showConferenceList && orgMembers && (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {(orgMembers as any[]).filter((member: any) => member.userId !== voiceContext?.userId).map((member: any) => {
                        const isAvailable = member.user?.presence?.status === 'available';
                        const isBusy = member.user?.presence?.status === 'busy';
                        
                        return (
                          <div key={member.id} className="flex gap-2 items-center">
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                              isAvailable ? 'bg-green-500' : 
                              isBusy ? 'bg-yellow-500' : 
                              'bg-gray-500'
                            }`} />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => addToConference({ 
                                userId: member.userId, 
                                name: member.user?.fullName || member.user?.email || 'teammate' 
                              })}
                              className="flex-1 justify-start h-9 text-xs border-border hover:bg-primary hover:border-primary hover:text-text-layer-primary transition-all"
                              data-testid={`button-conference-add-${member.id}`}
                            >
                              <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                              <span className="truncate">{member.user?.fullName || member.user?.email}</span>
                            </Button>
                          </div>
                        );
                      })}
                      {(!orgMembers || (orgMembers as any[]).filter((m: any) => m.userId !== voiceContext?.userId).length === 0) && (
                        <p className="text-xs text-text-layer-muted text-center py-2">No other team members available</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Phone Number Input */}
        {!isInCall && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Select value={selectedIso} onValueChange={(iso) => {
                const c = countryCodes.find((x) => x.iso === iso);
                if (!c) return;
                setPickerIso(iso);
                setCountryCode(c.dialCode);
              }}>
                <SelectTrigger className="w-20 bg-surface-elevated border-border">
                  <SelectValue placeholder="+1" />
                </SelectTrigger>
                <SelectContent className="z-[10001]">
                  {countryCodes.map((country) => (
                    <SelectItem key={country.iso} value={country.iso} title={country.name}>
                      {country.flag} {country.dialCode}{country.entitled ? '' : ' 🔒'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={phoneNumber}
                onChange={(e) => {
                  if (quickDialTimerRef.current) { clearTimeout(quickDialTimerRef.current); quickDialTimerRef.current = null; }
                  handleInput(e.target.value);
                }}
                onPaste={handlePaste}
                placeholder="Enter phone number"
                className="bg-surface-elevated border-border text-text-layer-primary"
              />
            </div>
            {formattedDisplay && formattedDisplay !== phoneNumber && (
              <p className="text-xs text-text-layer-muted mt-1 tracking-wider tabular-nums">
                {countryCode} {formattedDisplay}
              </p>
            )}

            {/* Caller ID Selection */}
            {availableNumbers.length > 0 && (
              <div>
                <label className="text-sm text-text-layer-muted mb-2 block">From:</label>
                <Select value={selectedCallerId} onValueChange={setSelectedCallerId}>
                  <SelectTrigger className="bg-surface-elevated border-border">
                    <SelectValue placeholder="Select caller ID" />
                  </SelectTrigger>
                  <SelectContent className="z-[10001]">
                    {/* Phone Numbers */}
                    {availableNumbers.map((number: string) => (
                      <SelectItem key={number} value={number}>
                        {number}
                      </SelectItem>
                    ))}
                    
                    {/* Verified Numbers */}
                    {(callSettings as any)?.verifiedNumbers?.filter((vn: any) => vn.canUseAsCallerId && !availableNumbers.includes(vn.number))
                      .map((verifiedNumber: any) => (
                        <SelectItem key={verifiedNumber.number} value={verifiedNumber.number}>
                          {verifiedNumber.number} (Verified){verifiedNumber.isPrimary ? ' (Primary)' : ''}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <CountryGateNotice gate={liveCountryGate} source="desktop_dialer_country" />

            {/* Trial Account Notice */}
            {isTrialing && (
              <div className="space-y-2">
                <TrialQuickDials testCalls={trialTestCalls} onDial={(n) => setPhoneNumber(n)} />
                <TrialDestinationNotice fullNumber={liveCountryGate && liveCountryGate.kind !== 'allowed' && !(liveCountryGate.kind === 'trial' && liveCountryGate.domestic) ? '' : displayNumber} testCalls={trialTestCalls} source="desktop_dialer_trial" onCallDemo={(n) => setPhoneNumber(n)} />
                <TrialTestCallsLeft testCalls={trialTestCalls} />
              </div>
            )}
          </div>
        )}

        {/* Keypad */}
        {showKeypad && (
          <div className="grid grid-cols-3 gap-3 mt-4 mx-auto w-full max-w-[248px]">
            {keypadButtons.map((button) => (
              <Button
                key={button.value}
                type="button"
                variant="outline"
                className="h-14 w-full bg-surface-elevated border-border hover:bg-surface-elevated active:bg-surface-hover text-text-layer-primary flex flex-col items-center justify-center"
                onClick={() => handleNumberInput(button.value)}
              >
                <span className="text-lg font-semibold leading-none">{button.label}</span>
                {button.sub && (
                  <span className="text-[10px] text-text-layer-muted tracking-widest mt-0.5">{button.sub}</span>
                )}
              </Button>
            ))}
          </div>
        )}

        {/* Recent Calls with Quick-Dial */}
        {!isInCall && (recentCallLogs as any[]).length > 0 && (
          <div className="mt-3 border-t border-border pt-3">
            <p className="text-xs text-text-layer-muted uppercase tracking-wider mb-2">Recent</p>
            {(recentCallLogs as any[]).slice(0, 3).map((call: any, i: number) => {
              const num = call.direction === 'inbound'
                ? (call.from || call.fromNumber || '')
                : (call.to || call.toNumber || '');
              return (
                <div key={call.id || i} className="flex items-center gap-2 py-1.5">
                  <span className="text-sm text-text-layer-secondary flex-1 truncate min-w-0">
                    {call.contactName || num}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      const detected = detectCountryFromE164(num);
                      if (detected) {
                        setCountryCode(detected);
                        setPhoneNumber(num.slice(detected === '+1CA' ? 2 : detected.length));
                      } else {
                        setPhoneNumber(num.replace(/^\+1/, ''));
                      }
                      if (quickDialTimerRef.current) clearTimeout(quickDialTimerRef.current);
                      quickDialTimerRef.current = setTimeout(() => handleCall(), 1500);
                    }}
                    className="w-7 h-7 rounded-full bg-green-900/40 border border-green-700/40 flex items-center justify-center hover:bg-green-800/50 active:scale-95 transition-all flex-shrink-0"
                    title="Quick dial"
                  >
                    <Phone className="w-3 h-3 text-green-400" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-center gap-3 mt-6">
          {isInCall ? (
            <>
              {/* Record Button */}
              <Button
                variant="outline"
                size="lg"
                onClick={toggleRecording}
                className={cn(
                  "rounded-full w-12 h-12 border-border",
                  isRecording ? "bg-red-600 hover:bg-red-700" : "bg-surface-elevated hover:bg-surface-elevated"
                )}
                title={isRecording ? "Stop Recording" : "Start Recording"}
              >
                <Radio className="w-5 h-5" />
              </Button>
              
              {/* Hold Button */}
              <Button
                variant="outline"
                size="lg"
                onClick={toggleHold}
                disabled={transferState === 'consulting'}
                className={cn(
                  "rounded-full w-12 h-12 border-border",
                  isOnHold ? "bg-yellow-600 hover:bg-yellow-700" : "bg-surface-elevated hover:bg-surface-elevated"
                )}
                title={isOnHold ? "Resume Call" : "Hold Call"}
                data-testid="button-hold"
              >
                {isOnHold ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
              </Button>
              
              {/* Mute Button */}
              <Button
                variant="outline"
                size="lg"
                onClick={toggleMute}
                className={cn(
                  "rounded-full w-12 h-12 border-border",
                  isMuted ? "bg-red-600 hover:bg-red-700" : "bg-surface-elevated hover:bg-surface-elevated"
                )}
              >
                {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </Button>
              
              {/* End Call Button */}
              <Button
                variant="destructive"
                size="lg"
                onClick={handleEndCall}
                className="rounded-full w-12 h-12 bg-red-600 hover:bg-red-700"
              >
                <PhoneOff className="w-5 h-5" />
              </Button>
            </>
          ) : (
            <Button
              variant="default"
              size="lg"
              onClick={handleCall}
              disabled={!phoneNumber.trim() || !isDeviceReady || !availableNumbers || availableNumbers.length === 0 || !balanceOK}
              className="rounded-full w-12 h-12 bg-green-600 hover:bg-green-700 disabled:bg-surface-hover"
              title={!balanceOK ? "Payment Required - Update payment method to make calls" : "Make Call"}
            >
              <PhoneCall className="w-5 h-5" />
            </Button>
          )}
        </div>

        {/* Status Messages */}
        {!balanceOK && (
          <div className="text-center">
            <div className="text-red-400 text-sm">
              <span className="text-xs text-red-400">
                Payment required - Update payment method to make calls
              </span>
            </div>
          </div>
        )}
        {balanceOK && !isDeviceReady && (
          <div className="text-center">
            <div className="text-red-400 text-sm">
              <span className="text-xs text-red-400">
                Voice service unavailable
              </span>
            </div>
          </div>
        )}
        {balanceOK && isDeviceReady && (!availableNumbers || availableNumbers.length === 0) && (
          <div className="text-center">
            <div className="text-amber-400 text-sm">
              <span className="text-xs text-amber-400">
                No phone numbers available for calling
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed top-0 right-0 h-full z-50 flex items-start justify-end pt-16 pr-4 pointer-events-none">
        <div className="pointer-events-auto">
          {dialerContent}
        </div>
      </div>
    );
  }

  return dialerContent;
}